import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";

import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import {
  AutomationRuleDraft,
  AutomationRuleTestResult,
  MatchingStrategyType,
  RuleListFilter,
  WhatsAppAutomationRule,
} from "../../models/automationRule.model";
import {
  createAutomationRule,
  deleteAutomationRule,
  getAutomationRules,
  testAutomationRule,
  updateAutomationRule,
} from "../../services/automationRules.service";

const FILTERS: Array<{ value: RuleListFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "needs_review", label: "Needs Review" },
];

const STRATEGIES: Array<{
  value: MatchingStrategyType;
  label: string;
  helper: string;
}> = [
  {
    value: "similarity_score",
    label: "Smart Match",
    helper: "Best for normal customer phrasing with close wording.",
  },
  {
    value: "exact_match",
    label: "Exact Match",
    helper: "Only matches when the incoming message is an exact phrase.",
  },
  {
    value: "regex",
    label: "Advanced Pattern",
    helper: "Use when you want explicit pattern matching.",
  },
  {
    value: "intent_classification",
    label: "Intent Match",
    helper: "Reserve for broader intent-based routing.",
  },
];

const DEFAULT_FALLBACK_MESSAGE =
  "Thanks for your message. Our team will help you shortly.";

function createDefaultDraft(): AutomationRuleDraft {
  return {
    automationMode: "rule_based",
    intentName: "",
    intentDisplayName: "",
    intentDescription: "",
    trainingPhrases: ["", "", ""],
    matchingStrategy: {
      type: "similarity_score",
      similarityThreshold: 0.78,
      regexPattern: "",
    },
    reply: [{ type: "text", message: "" }],
    fallback: {
      enabled: true,
      message: DEFAULT_FALLBACK_MESSAGE,
    },
    handoff: {
      enabled: true,
      notifyHuman: true,
    },
    priority: 1,
    isActive: true,
  };
}

function createDraftFromRule(
  rule: WhatsAppAutomationRule,
): AutomationRuleDraft {
  return {
    id: rule.id,
    automationMode: rule.automationMode,
    intentName: rule.intentName,
    intentDisplayName: rule.intentDisplayName,
    intentDescription: rule.intentDescription,
    trainingPhrases: [...rule.trainingPhrases],
    matchingStrategy: { ...rule.matchingStrategy },
    reply: [...rule.reply],
    fallback: { ...rule.fallback },
    handoff: { ...rule.handoff },
    priority: rule.priority,
    isActive: rule.isActive,
  };
}

function getStrategyLabel(type: MatchingStrategyType) {
  return STRATEGIES.find((item) => item.value === type)?.label ?? type;
}

function needsReview(rule: WhatsAppAutomationRule) {
  return (
    rule.matchingStrategy.type === "intent_classification" ||
    (rule.matchingStrategy.type === "similarity_score" &&
      rule.matchingStrategy.similarityThreshold >= 0.9)
  );
}

function formatDateTime(value: string) {
  if (!value) {
    return "Unknown";
  }

  return new Date(value).toLocaleString();
}

const AutomationRulesConfig: React.FC = () => {
  const { settings } = useContext(BusinessSettingsContext);
  const businessId = settings?.businessId;

  const [rules, setRules] = useState<WhatsAppAutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RuleListFilter>("all");
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<AutomationRuleDraft>(createDefaultDraft());
  const [saving, setSaving] = useState(false);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<AutomationRuleTestResult | null>(
    null,
  );

  const loadRules = useCallback(async () => {
    if (!businessId) {
      setRules([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getAutomationRules(businessId);
      setRules(response.items);
    } catch (loadError) {
      console.error(loadError);
      setError("Unable to load auto replies right now.");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const filteredRules = useMemo(() => {
    return rules.filter((rule) => {
      const searchHit =
        !search.trim() ||
        [
          rule.intentDisplayName,
          rule.intentDescription,
          ...rule.trainingPhrases,
          rule.reply[0]?.message ?? "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(search.trim().toLowerCase());

      const filterHit =
        filter === "all"
          ? true
          : filter === "active"
            ? rule.isActive
            : filter === "inactive"
              ? !rule.isActive
              : needsReview(rule);

      return searchHit && filterHit;
    });
  }, [filter, rules, search]);

  const cleanedTrainingPhrases = useMemo(() => {
    return draft.trainingPhrases.map((item) => item.trim()).filter(Boolean);
  }, [draft.trainingPhrases]);

  const canSave = useMemo(() => {
    const hasRegex =
      draft.matchingStrategy.type !== "regex" ||
      draft.matchingStrategy.regexPattern.trim().length > 0;

    return (
      draft.intentDisplayName.trim().length > 0 &&
      cleanedTrainingPhrases.length > 0 &&
      (draft.reply[0]?.message ?? "").trim().length > 0 &&
      hasRegex
    );
  }, [cleanedTrainingPhrases.length, draft]);

  const openCreateDialog = () => {
    setDraft(createDefaultDraft());
    setEditorOpen(true);
  };

  const openEditDialog = (rule: WhatsAppAutomationRule) => {
    setDraft(createDraftFromRule(rule));
    setEditorOpen(true);
  };

  const closeEditorDialog = () => {
    if (saving) {
      return;
    }

    setEditorOpen(false);
    setDraft(createDefaultDraft());
  };

  const handleSaveRule = async () => {
    if (!businessId || !canSave) {
      return;
    }

    const payload: AutomationRuleDraft = {
      ...draft,
      intentName: draft.intentDisplayName.trim(),
      intentDisplayName: draft.intentDisplayName.trim(),
      intentDescription: draft.intentDescription.trim(),
      trainingPhrases: cleanedTrainingPhrases,
      reply: [
        {
          type: "text",
          message: (draft.reply[0]?.message ?? "").trim(),
        },
      ],
      fallback: {
        ...draft.fallback,
        message: draft.fallback.message.trim() || DEFAULT_FALLBACK_MESSAGE,
      },
      priority: Number.isFinite(draft.priority) ? Math.max(1, draft.priority) : 1,
    };

    try {
      setSaving(true);
      setError(null);

      if (draft.id) {
        await updateAutomationRule(businessId, draft.id, payload);
      } else {
        await createAutomationRule(businessId, payload);
      }

      setEditorOpen(false);
      setDraft(createDefaultDraft());
      await loadRules();
    } catch (saveError) {
      console.error(saveError);
      setError("Unable to save this auto reply. Please review the fields.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (rule: WhatsAppAutomationRule) => {
    if (!businessId) {
      return;
    }

    try {
      setBusyRuleId(rule.id);
      await updateAutomationRule(businessId, rule.id, {
        ...createDraftFromRule(rule),
        isActive: !rule.isActive,
      });
      await loadRules();
    } catch (toggleError) {
      console.error(toggleError);
      setError("Unable to update rule status right now.");
    } finally {
      setBusyRuleId(null);
    }
  };

  const handleDeleteRule = async (rule: WhatsAppAutomationRule) => {
    if (!businessId) {
      return;
    }

    const confirmed = window.confirm(
      `Delete the auto reply "${rule.intentDisplayName}"?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setBusyRuleId(rule.id);
      await deleteAutomationRule(businessId, rule.id);
      await loadRules();
    } catch (deleteError) {
      console.error(deleteError);
      setError("Unable to delete this auto reply right now.");
    } finally {
      setBusyRuleId(null);
    }
  };

  const handleTestReply = async () => {
    if (!businessId || !testMessage.trim()) {
      return;
    }

    try {
      setTestLoading(true);
      setError(null);
      const response = await testAutomationRule(businessId, testMessage.trim());
      setTestResult(response);
    } catch (testError) {
      console.error(testError);
      setError("Unable to test the incoming message right now.");
    } finally {
      setTestLoading(false);
    }
  };

  if (!businessId) {
    return (
      <Box p={4}>
        <Typography color="text.secondary">
          Please complete Business Settings to configure incoming message rules.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>
            Incoming Message Rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure how an incoming customer message maps to a rule and the
            reply your bot should send.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={600}>
              Test Incoming Message
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a sample WhatsApp message to see which rule matches and what
              reply will be sent.
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Incoming customer message"
                placeholder="price kya hai?"
                value={testMessage}
                onChange={(event) => setTestMessage(event.target.value)}
              />
              <Button
                variant="contained"
                startIcon={<PlayArrowOutlinedIcon />}
                onClick={handleTestReply}
                disabled={testLoading || !testMessage.trim()}
                sx={{ minWidth: 180 }}
              >
                {testLoading ? "Testing..." : "Test Reply"}
              </Button>
            </Stack>

            {testResult ? (
              <Alert severity={testResult.matched ? "success" : "warning"}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {testResult.matched
                      ? `Matched rule: ${testResult.intentDisplayName || "Unnamed rule"}`
                      : "No rule matched this message"}
                  </Typography>
                  <Typography variant="body2">
                    Reply:{" "}
                    {testResult.reply?.message || DEFAULT_FALLBACK_MESSAGE}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Strategy: {testResult.strategy || "fallback"} | Confidence:{" "}
                    {Math.round((testResult.confidence || 0) * 100)}% | Fallback:{" "}
                    {testResult.usedFallback ? "Yes" : "No"}
                  </Typography>
                </Stack>
              </Alert>
            ) : null}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", lg: "center" }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Auto Replies ({rules.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add examples of incoming messages, decide how to match them,
                  and write the reply once.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={openCreateDialog}
              >
                Create Auto Reply
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Search rules"
                placeholder="pricing, booking, delivery..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <TextField
                select
                label="Filter"
                value={filter}
                onChange={(event) =>
                  setFilter(event.target.value as RuleListFilter)
                }
                sx={{ minWidth: { xs: "100%", md: 220 } }}
              >
                {FILTERS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            {loading ? (
              <Box py={6} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : filteredRules.length ? (
              <Stack spacing={2}>
                {filteredRules.map((rule) => (
                  <Card key={rule.id} variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.5}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                        >
                          <Box>
                            <Typography variant="h6" fontWeight={700}>
                              {rule.intentDisplayName}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {rule.intentDescription ||
                                "No extra description provided."}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                              label={rule.isActive ? "Active" : "Inactive"}
                              color={rule.isActive ? "success" : "default"}
                              size="small"
                            />
                            <Chip
                              label={getStrategyLabel(
                                rule.matchingStrategy.type,
                              )}
                              variant="outlined"
                              size="small"
                            />
                            <Chip
                              label={`Priority ${rule.priority}`}
                              variant="outlined"
                              size="small"
                            />
                            {needsReview(rule) ? (
                              <Chip
                                label="Needs Review"
                                color="warning"
                                size="small"
                              />
                            ) : null}
                          </Stack>
                        </Stack>

                        <Divider />

                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} mb={1}>
                            Incoming message examples
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {rule.trainingPhrases.map((phrase, index) => (
                              <Chip
                                key={`${rule.id}-${index}`}
                                label={phrase}
                                variant="outlined"
                                sx={{ mb: 1 }}
                              />
                            ))}
                          </Stack>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} mb={1}>
                            Reply
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {rule.reply[0]?.message || DEFAULT_FALLBACK_MESSAGE}
                          </Typography>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Updated {formatDateTime(rule.updatedAt)}
                        </Typography>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => openEditDialog(rule)}
                            disabled={busyRuleId === rule.id}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => handleToggleActive(rule)}
                            disabled={busyRuleId === rule.id}
                          >
                            {busyRuleId === rule.id
                              ? "Updating..."
                              : rule.isActive
                                ? "Deactivate"
                                : "Activate"}
                          </Button>
                          <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleDeleteRule(rule)}
                            disabled={busyRuleId === rule.id}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">
                {search || filter !== "all"
                  ? "No rules match the current search or filter."
                  : "No auto replies configured yet. Create the first rule to map an incoming message to a reply."}
              </Alert>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={editorOpen}
        onClose={closeEditorDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {draft.id ? "Edit Auto Reply" : "Create Auto Reply"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={0.5}>
            <Alert severity="info">
              Configure the full flow: incoming message examples, matching rule,
              and the final reply.
            </Alert>

            <TextField
              fullWidth
              label="Rule name"
              placeholder="Pricing / Charges"
              value={draft.intentDisplayName}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  intentDisplayName: event.target.value,
                  intentName: event.target.value,
                }))
              }
              helperText="What is the customer asking about?"
            />

            <TextField
              fullWidth
              label="Description"
              placeholder="Customer is asking about pricing, charges, or plans."
              value={draft.intentDescription}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  intentDescription: event.target.value,
                }))
              }
              multiline
              minRows={3}
            />

            <TextField
              fullWidth
              label="Incoming message examples"
              placeholder={"price kya hai?\nhow much does it cost?\nplan details"}
              value={draft.trainingPhrases.join("\n")}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  trainingPhrases: event.target.value.split("\n"),
                }))
              }
              helperText="Add one example incoming message per line."
              multiline
              minRows={5}
            />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                fullWidth
                label="Matching strategy"
                value={draft.matchingStrategy.type}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    matchingStrategy: {
                      ...prev.matchingStrategy,
                      type: event.target.value as MatchingStrategyType,
                    },
                  }))
                }
                helperText={
                  STRATEGIES.find(
                    (item) => item.value === draft.matchingStrategy.type,
                  )?.helper
                }
              >
                {STRATEGIES.map((strategy) => (
                  <MenuItem key={strategy.value} value={strategy.value}>
                    {strategy.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={draft.priority}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    priority: Number(event.target.value || 1),
                  }))
                }
                inputProps={{ min: 1 }}
                helperText="Lower numbers are evaluated first."
              />
            </Stack>

            {draft.matchingStrategy.type === "similarity_score" ||
            draft.matchingStrategy.type === "intent_classification" ? (
              <TextField
                fullWidth
                type="number"
                label="Similarity threshold"
                value={draft.matchingStrategy.similarityThreshold}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    matchingStrategy: {
                      ...prev.matchingStrategy,
                      similarityThreshold: Number(event.target.value || 0),
                    },
                  }))
                }
                inputProps={{ min: 0, max: 1, step: 0.01 }}
                helperText="Use a value between 0 and 1."
              />
            ) : null}

            {draft.matchingStrategy.type === "regex" ? (
              <TextField
                fullWidth
                label="Regex pattern"
                placeholder="(price|charges|cost|plan)"
                value={draft.matchingStrategy.regexPattern}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    matchingStrategy: {
                      ...prev.matchingStrategy,
                      regexPattern: event.target.value,
                    },
                  }))
                }
              />
            ) : null}

            <TextField
              fullWidth
              label="Reply message"
              placeholder="Our pricing starts from Rs 999/month."
              value={draft.reply[0]?.message ?? ""}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  reply: [{ type: "text", message: event.target.value }],
                }))
              }
              multiline
              minRows={4}
            />

            <TextField
              fullWidth
              label="Fallback message"
              value={draft.fallback.message}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  fallback: {
                    ...prev.fallback,
                    message: event.target.value,
                  },
                }))
              }
              multiline
              minRows={2}
            />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent="space-between"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.fallback.enabled}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        fallback: {
                          ...prev.fallback,
                          enabled: event.target.checked,
                        },
                      }))
                    }
                  />
                }
                label="Enable fallback"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.handoff.notifyHuman}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        handoff: {
                          ...prev.handoff,
                          notifyHuman: event.target.checked,
                        },
                      }))
                    }
                  />
                }
                label="Notify human"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.isActive}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        isActive: event.target.checked,
                      }))
                    }
                  />
                }
                label="Rule active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditorDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRule}
            disabled={!canSave || saving}
          >
            {saving ? "Saving..." : "Save Auto Reply"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutomationRulesConfig;
